// src/components/ai/copilot/VoiceCaptureButton.js
//
// Mic button that uses the Web Speech API for browser-native speech-to-text.
// Places the transcript in the notes field. Hidden if SpeechRecognition
// is not supported (Firefox, older browsers).

import React, { useState, useRef, useCallback } from 'react';

const SpeechRecognition =
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

/**
 * @param {object} props
 * @param {(text: string) => void} props.onTranscript — called with final transcript
 * @param {boolean} [props.disabled]
 */
function VoiceCaptureButton({ onTranscript, disabled = false }) {
  const [recording, setRecording] = useState(false);
  const recognitionRef = useRef(null);

  const supported = Boolean(SpeechRecognition);

  const startRecording = useCallback(() => {
    if (!SpeechRecognition || disabled || recording) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    let transcript = '';

    recognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          transcript += event.results[i][0].transcript + ' ';
        }
      }
    };

    recognition.onerror = () => {
      setRecording(false);
      recognitionRef.current = null;
    };

    recognition.onend = () => {
      setRecording(false);
      recognitionRef.current = null;
      if (transcript.trim()) {
        onTranscript(transcript.trim());
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
    setRecording(true);
  }, [disabled, recording, onTranscript]);

  const stopRecording = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  }, []);

  if (!supported) return null;

  return (
    <button
      type="button"
      onClick={recording ? stopRecording : startRecording}
      disabled={disabled}
      aria-label={recording ? 'Stop recording' : 'Start voice capture'}
      className={`
        rounded-full p-2.5 transition-colors
        ${recording
          ? 'bg-rose-500 text-white shadow-lg animate-pulse'
          : 'bg-slate-100 text-slate-500 hover:bg-violet-50 hover:text-violet-600'
        }
        disabled:opacity-50 disabled:cursor-not-allowed
      `}
    >
      {recording ? (
        // Stop icon (square)
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <rect x="5" y="5" width="10" height="10" rx="1" />
        </svg>
      ) : (
        // Mic icon
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 01-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
        </svg>
      )}
    </button>
  );
}

export default VoiceCaptureButton;
